import React from 'react'
import { connect } from 'react-redux'
import { noop } from 'lodash-es'
import { createStructuredSelector } from 'reselect'
import { DealDetail, AuthorisedUser } from 'api'
import { ShowDialogArgs, NotificationActions, NotificationType } from 'store/notification'
import { ModalConsumerProps, UnhappyModal, withModal } from 'view/shared/modal'
import { ModalTitle, ModalWindow, ModalFooter } from 'view/components/panels'
import {
  OpenFinWindow,
  Content,
  DetailsContainer,
  TabbedContent,
  AllocationContainer,
  FundAssignmentUploadDropzone
} from '../allocationDialogStyles'
import { Tabs } from 'view/components/tabs'
import { AvailableTradingEntitiesPanel } from './AvailableTradingEntitiesPanel'
import { AssignmentPanel } from './AssignmentPanel'
import { AssignmentHeader } from './AssignmentHeader'
import { DialogRow, DialogField } from 'view/components/inputs'
import { TextButton, TextButtonType } from 'view/components/buttons'
import { createDealDetailSelector } from 'selectors/dealSelectors'
import { createAuthorisedUserSelector } from 'selectors/connectionSelectors'
import { isOpenFin } from 'utils'
import { Locale, Translation } from 'locale'
import { TradingEntityAssignmentUploadDialog } from './upload/TradingEntityAssignmentUploadDialog'
import { FundAssignmentDialogToolbar } from './FundAssignmentDialogToolbar'
import { pendingTradingToFundTradingEntityAssignmentMapper } from './upload/parseTradingEntityAssignmentSpreadsheet'
import { getTradingEntityAssignmentTradingType } from 'model/dealUtils'
import { useFundAssignment } from './useFundAssignment'

export type FundAssignmentDialogProps = OwnProps & StateProps & DispatchProps

interface OwnProps extends ModalConsumerProps {
  dealId: string
  onClose?: () => unknown
}

interface StateProps {
  dealDetail: DealDetail | undefined
  authorisedUser: AuthorisedUser
}

interface DispatchProps {
  onShowDialog: (dialogArgs: ShowDialogArgs) => unknown
}

const dealIdSelector = (_: {}, props: FundAssignmentDialogProps) => props.dealId

const mapStateToProps = createStructuredSelector({
  dealDetail: createDealDetailSelector(dealIdSelector),
  authorisedUser: createAuthorisedUserSelector()
})

const mapDispatchToProps = {
  onShowDialog: NotificationActions.showDialog
}

export const FundAssignmentDialogComponent: React.FC<FundAssignmentDialogProps> = props => {
  const { dealDetail, authorisedUser, onClose, onShowDialog, showModal } = props

  const {
    assignmentDetails,
    isAssignDisabled,
    onAddNewAssignment,
    onAssign,
    onCancelAssignments,
    onRemoveAssignment,
    onSetTrimOrFill,
    onUpdateAssignment,
    onUploadAssignments
  } = useFundAssignment(dealDetail)

  if (!dealDetail || !dealDetail.orderFund || !dealDetail.deal.fundAssignment || !assignmentDetails) {
    return <UnhappyModal title={Locale.translate(Translation.SC_NO_FUND_DETAILS)} />
  }

  if (dealDetail.deal.fundAssignment.userIdWithLock !== authorisedUser.username) {
    return (
      <UnhappyModal
        title={dealDetail.orderFund.publishedName}
        message={Locale.translate(Translation.SC_NOT_ASSIGNMENT_AUTHOR)}
        onClose={onClose}
      />
    )
  }

  const handleCancel = () => {
    onCancelAssignments(dealDetail.deal.dealId)
    handleClose()
  }

  const handleAssign = () => {
    const remainingAssignmentRequired = dealDetail.deal.fundAssignment?.remainingAssignmentRequired || 0
    if (remainingAssignmentRequired === 0) {
      onAssign(dealDetail.deal.dealId)
      handleClose()
    } else {
      const remainingAssignmentText = Locale.formatNumber(Math.abs(remainingAssignmentRequired), { minDp: 2 })
      onShowDialog({
        type: NotificationType.Confirm,
        titleText:
          remainingAssignmentRequired < 0
            ? Locale.translate(Translation.TC_PROMPT_TITLE_OVER_ASSIGNMENT)
            : Locale.translate(Translation.TC_PROMPT_TITLE_UNDER_ASSIGNMENT),
        messageText:
          remainingAssignmentRequired < 0
            ? Locale.translate(Translation.SC_PROMPT_MSG_ACCEPT_OVER_ASSIGNMENT, { remainingAssignmentText })
            : Locale.translate(Translation.SC_PROMPT_MSG_ACCEPT_UNDER_ASSIGNMENT, { remainingAssignmentText }),
        buttons: [Translation.TC_BUTTON_YES, Translation.TC_BUTTON_NO],
        defaultButton: Translation.TC_BUTTON_YES,
        cancelButton: Translation.TC_BUTTON_NO,
        responseMap: {
          [Translation.TC_BUTTON_YES]: [
            () => {
              onAssign(dealDetail.deal.dealId)
              handleClose()
            }
          ]
        }
      })
    }
  }

  const handleClose = () => {
    if (onClose) {
      onClose()
    }

    if (isOpenFin()) {
      fin.desktop.Window.getCurrent().close()
    }
  }

  const showTradingEntityAssignmentUploadDialog = (file?: File, clipboard?: string) =>
    showModal(
      <TradingEntityAssignmentUploadDialog
        assignmentDetails={assignmentDetails}
        dealDetail={dealDetail}
        initialClipboard={clipboard}
        initialFile={file}
        onComplete={async tradingEntityAssignmentUpdates => {
          onUploadAssignments(tradingEntityAssignmentUpdates.map(pendingTradingToFundTradingEntityAssignmentMapper))
        }}
      />
    )

  const DialogContent = (
    <>
      <Content>
        <DetailsContainer>
          <Tabs
            dataTestId={'FundAssignmentDialog'}
            selectedTab={Translation.TC_TRADING_ENTITIES}
            onChangeTab={noop}
            tabs={[Translation.TC_TRADING_ENTITIES]}
          />
          <TabbedContent>
            <AvailableTradingEntitiesPanel assignmentDetails={assignmentDetails} onAddNewAssignment={onAddNewAssignment} />
          </TabbedContent>
        </DetailsContainer>
        <AllocationContainer>
          {/* Assign by notional if it exists even if trading type is CASH */}
          <AssignmentPanel
            totalAmountRequired={dealDetail.deal.notional ?? dealDetail.deal.startConsideration}
            assignmentDetails={assignmentDetails}
            fundAssignment={dealDetail.deal.fundAssignment}
            tradingType={getTradingEntityAssignmentTradingType(dealDetail)}
            userPreference={authorisedUser.userPreference}
            onSetTrimOrFill={onSetTrimOrFill}
            onRemoveAssignment={onRemoveAssignment}
            onTradingEntityAssignmentChange={(tradingEntityId, updatedPartialAssignment, index) =>
              onUpdateAssignment({ tradingEntityId, ...updatedPartialAssignment }, index)
            }
          />
        </AllocationContainer>
      </Content>
      <ModalFooter>
        <DialogRow>
          <DialogField>
            <TextButton dataTestId={'FundAssignmentDialog-TextButton-CancelButton'} type={TextButtonType.Tertiary} onClick={handleCancel}>
              {Locale.translate(Translation.TC_BUTTON_CANCEL)}
            </TextButton>
          </DialogField>

          <DialogField flex={0}>
            <TextButton
              dataTestId={'FundAssignmentDialog-TextButton-AssignButton'}
              disabled={isAssignDisabled}
              type={TextButtonType.Primary}
              onClick={handleAssign}>
              {Locale.translate(Translation.TC_BUTTON_ASSIGN)}
            </TextButton>
          </DialogField>
        </DialogRow>
      </ModalFooter>
    </>
  )

  return isOpenFin() ? (
    <OpenFinWindow>
      <ModalTitle>
        <AssignmentHeader name={dealDetail.orderFund.publishedName} expiryTime={null}>
          <FundAssignmentDialogToolbar onShowFundAssignmentDialogModal={() => showTradingEntityAssignmentUploadDialog()} />
        </AssignmentHeader>
      </ModalTitle>
      <FundAssignmentUploadDropzone
        onDropFiles={files => showTradingEntityAssignmentUploadDialog(files[0])}
        onPaste={clipboard => showTradingEntityAssignmentUploadDialog(undefined, clipboard)}
        render={() => DialogContent}
      />
    </OpenFinWindow>
  ) : (
    <ModalWindow
      title={
        <AssignmentHeader name={dealDetail.orderFund.publishedName} expiryTime={null}>
          <FundAssignmentDialogToolbar onShowFundAssignmentDialogModal={() => showTradingEntityAssignmentUploadDialog()} />
        </AssignmentHeader>
      }>
      <FundAssignmentUploadDropzone
        onDropFiles={files => showTradingEntityAssignmentUploadDialog(files[0])}
        onPaste={clipboard => showTradingEntityAssignmentUploadDialog(undefined, clipboard)}
        render={() => DialogContent}
      />
    </ModalWindow>
  )
}

export const FundAssignmentDialog = withModal(
  connect<StateProps, DispatchProps, OwnProps>(mapStateToProps, mapDispatchToProps)(FundAssignmentDialogComponent)
)
